// src/pages/CRM/HelpDesk/TicketDetail.js

import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box, Typography, Paper, Grid, Button, Chip, Avatar, IconButton,
  CircularProgress, Alert, Divider, Stack, Tabs, Tab, List, ListItem,
  ListItemAvatar, ListItemText, TextField, FormControlLabel, Checkbox,
  Card, CardContent, MenuItem, Select, FormControl, InputLabel
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Send as SendIcon,
  Person as PersonIcon,
  Business as BusinessIcon,
  AccessTime as TimeIcon,
  History as HistoryIcon,
  Comment as CommentIcon
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { hasPermission } from '../../../../utils/permissionUtils';
import { ticketService } from './helpDeskService';
import {
  TicketStatusLabels, TicketStatusColors, TicketStatusOptions,
  TicketPriorityLabels, TicketPriorityColors, TicketPriorityOptions,
  TicketSourceLabels, formatDateTime, formatTimeAgo, getInitials
} from './helpDeskEnums';
import TicketForm from './TicketForm';

const TicketDetail = () => {
  const { chatbotId, ticketId } = useParams();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  const [loading, setLoading] = useState(true);
  const [ticket, setTicket] = useState(null);
  const [canView, setCanView] = useState(false);
  const [canModify, setCanModify] = useState(false);
  const [tabValue, setTabValue] = useState(0);
  const [editOpen, setEditOpen] = useState(false);

  // Comment form
  const [comment, setComment] = useState('');
  const [isInternal, setIsInternal] = useState(false);
  const [submittingComment, setSubmittingComment] = useState(false);

  // Status update
  const [updatingStatus, setUpdatingStatus] = useState(false);

  useEffect(() => {
    document.title = 'Ticket Details | Help Desk';
    checkPermissions();
  }, []);

  useEffect(() => {
    if (canView && ticketId) {
      loadTicket();
    }
  }, [canView, ticketId]);

  const checkPermissions = async () => {
    try {
      const viewPerm = await hasPermission('crm', 'view');
      const modifyPerm = await hasPermission('crm', 'add|edit');
      setCanView(viewPerm);
      setCanModify(modifyPerm);
      if (!viewPerm) {
        setLoading(false);
      }
    } catch (err) {
      console.error('Error checking permissions:', err);
      setLoading(false);
    }
  };

  const loadTicket = async () => {
    setLoading(true);
    try {
      const response = await ticketService.getById(ticketId);
      if (response.success) {
        setTicket(response.data);
        document.title = `${response.data.ticket_number} | Help Desk`;
      } else {
        enqueueSnackbar('Failed to load ticket', { variant: 'error' });
      }
    } catch (err) {
      enqueueSnackbar('Failed to load ticket', { variant: 'error' });
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (newStatus) => {
    setUpdatingStatus(true);
    try {
      const response = await ticketService.update(ticketId, { status: newStatus });
      if (response.success) {
        setTicket(response.data);
        enqueueSnackbar('Status updated successfully', { variant: 'success' });
      }
    } catch (err) {
      enqueueSnackbar('Failed to update status', { variant: 'error' });
    } finally {
      setUpdatingStatus(false);
    }
  };

  const handleAddComment = async () => {
    if (!comment.trim()) return;

    setSubmittingComment(true);
    try {
      const response = await ticketService.addComment(ticketId, {
        content: comment,
        is_internal: isInternal
      });
      if (response.success) {
        setComment('');
        setIsInternal(false);
        loadTicket(); // Reload to get updated comments
        enqueueSnackbar('Comment added successfully', { variant: 'success' });
      }
    } catch (err) {
      enqueueSnackbar('Failed to add comment', { variant: 'error' });
    } finally {
      setSubmittingComment(false);
    }
  };

  const handleEditClose = (refresh) => {
    setEditOpen(false);
    if (refresh) {
      loadTicket();
    }
  };

  const getSLAStatusDisplay = () => {
    if (!ticket?.sla_status) return null;
    const { status, label, time_left } = ticket.sla_status;

    let color = 'default';
    if (status === 'breached') color = 'error';
    else if (status === 'warning') color = 'warning';
    else if (status === 'met' || status === 'on_track') color = 'success';

    return (
      <Chip
        label={`${label}${time_left ? ` - ${time_left}` : ''}`}
        color={color}
        size="small"
      />
    );
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400, mt: { xs: 2, sm: 10 } }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!canView) {
    return (
      <Alert severity="error" sx={{ mt: { xs: 2, sm: 10 }, mx: 3 }}>
        <strong>Access Denied</strong><br />
        You don't have permission to view this ticket.
      </Alert>
    );
  }

  if (!ticket) {
    return (
      <Alert severity="error" sx={{ mt: { xs: 2, sm: 10 }, mx: 3 }}>
        Ticket not found
      </Alert>
    );
  }

  return (
    <Box sx={{ p: { xs: 1.5, sm: 3 }, mt: { xs: 2, sm: 10 } }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <IconButton onClick={() => navigate(`/chatbot/${chatbotId}/crm/helpdesk/tickets`)}>
            <ArrowBackIcon />
          </IconButton>
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1, flexWrap: 'wrap' }}>
              <Typography variant="h4" fontWeight={700} sx={{ fontSize: { xs: '1.5rem', sm: '2.125rem' } }}>
                {ticket.ticket_number}
              </Typography>
              <Chip
                label={TicketStatusLabels[ticket.status]}
                color={TicketStatusColors[ticket.status]}
              />
              <Chip
                label={TicketPriorityLabels[ticket.priority]}
                color={TicketPriorityColors[ticket.priority]}
                variant="outlined"
              />
              {getSLAStatusDisplay()}
            </Box>
            <Typography variant="h6" color="text.secondary">
              {ticket.subject}
            </Typography>
          </Box>
        </Box>
        {canModify && (
          <Button
            variant="outlined"
            size="small"
            startIcon={<EditIcon />}
            onClick={() => setEditOpen(true)}
          >
            Edit
          </Button>
        )}
      </Box>

      <Grid container spacing={3}>
        {/* Main Content */}
        <Grid item xs={12} md={8}>
          <Paper sx={{ mb: 3 }}>
            <Tabs value={tabValue} onChange={(e, v) => setTabValue(v)} variant="scrollable" scrollButtons="auto">
              <Tab icon={<CommentIcon />} label="Comments" />
              <Tab icon={<HistoryIcon />} label="History" />
            </Tabs>
            <Divider />

            {tabValue === 0 && (
              <Box sx={{ p: { xs: 1.5, sm: 3 }, px: { xs: 1.5, sm: 4 } }}>
                {/* Description */}
                {ticket.description && (
                  <Card variant="outlined" sx={{ mb: 3 }}>
                    <CardContent>
                      <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                        Description
                      </Typography>
                      <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                        {ticket.description}
                      </Typography>
                    </CardContent>
                  </Card>
                )}

                {/* Add Comment Form */}
                {canModify && (
                  <Box sx={{ mb: 3 }}>
                    <TextField
                      fullWidth
                      multiline
                      rows={3}
                      placeholder="Add a comment..."
                      value={comment}
                      onChange={(e) => setComment(e.target.value)}
                      sx={{ mb: 1 }}
                    />
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 2 }}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={isInternal}
                            onChange={(e) => setIsInternal(e.target.checked)}
                            size="small"
                          />
                        }
                        label="Internal note (not visible to customer)"
                      />
                      <Button
                        variant="contained"
                        endIcon={<SendIcon />}
                        onClick={handleAddComment}
                        disabled={!comment.trim() || submittingComment}
                      >
                        {submittingComment ? 'Adding...' : 'Add Comment'}
                      </Button>
                    </Box>
                  </Box>
                )}

                {/* Comments List */}
                <Typography variant="h6" gutterBottom>
                  Comments ({ticket.comments?.length || 0})
                </Typography>
                {(!ticket.comments || ticket.comments.length === 0) ? (
                  <Typography color="text.secondary" sx={{ py: 2 }}>
                    No comments yet
                  </Typography>
                ) : (
                  <List>
                    {ticket.comments.map((c, index) => (
                      <React.Fragment key={c.id}>
                        <ListItem alignItems="flex-start" sx={{ px: 0 }}>
                          <ListItemAvatar>
                            <Avatar sx={{ bgcolor: c.is_internal ? 'warning.main' : 'primary.main' }}>
                              {getInitials(c.author_name)}
                            </Avatar>
                          </ListItemAvatar>
                          <ListItemText
                            primary={
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Typography variant="subtitle2">
                                  {c.author_name || 'Unknown'}
                                </Typography>
                                {c.is_internal && (
                                  <Chip label="Internal" size="small" color="warning" />
                                )}
                                <Typography variant="caption" color="text.secondary">
                                  {formatTimeAgo(c.created_at)}
                                </Typography>
                              </Box>
                            }
                            secondary={
                              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', mt: 1 }}>
                                {c.content}
                              </Typography>
                            }
                          />
                        </ListItem>
                        {index < ticket.comments.length - 1 && <Divider component="li" />}
                      </React.Fragment>
                    ))}
                  </List>
                )}
              </Box>
            )}

            {tabValue === 1 && (
              <Box sx={{ p: { xs: 1.5, sm: 3 }, px: { xs: 1.5, sm: 4 } }}>
                <Typography variant="h6" gutterBottom>
                  Status History
                </Typography>
                {(!ticket.status_history || ticket.status_history.length === 0) ? (
                  <Typography color="text.secondary">No history available</Typography>
                ) : (
                  <List>
                    {ticket.status_history.map((h, index) => (
                      <ListItem key={h.id} sx={{ px: 0 }}>
                        <ListItemAvatar>
                          <Avatar sx={{ bgcolor: 'grey.300' }}>
                            <HistoryIcon color="action" />
                          </Avatar>
                        </ListItemAvatar>
                        <ListItemText
                          primary={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              {h.old_status && (
                                <>
                                  <Chip
                                    size="small"
                                    label={TicketStatusLabels[h.old_status]}
                                    variant="outlined"
                                  />
                                  <Typography>→</Typography>
                                </>
                              )}
                              <Chip
                                size="small"
                                label={TicketStatusLabels[h.new_status]}
                                color={TicketStatusColors[h.new_status]}
                              />
                            </Box>
                          }
                          secondary={
                            <Typography variant="caption" color="text.secondary">
                              {h.changer_name || 'System'} • {formatDateTime(h.changed_at)}
                              {h.notes && ` • ${h.notes}`}
                            </Typography>
                          }
                        />
                      </ListItem>
                    ))}
                  </List>
                )}
              </Box>
            )}
          </Paper>
        </Grid>

        {/* Sidebar */}
        <Grid item xs={12} md={4}>
          <Stack spacing={3}>
            {/* Quick Actions */}
            {canModify && (
              <Paper sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom>
                  Quick Actions
                </Typography>
                <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                  <InputLabel>Update Status</InputLabel>
                  <Select
                    value={ticket.status}
                    label="Update Status"
                    onChange={(e) => handleStatusChange(e.target.value)}
                    disabled={updatingStatus}
                  >
                    {TicketStatusOptions.map(opt => (
                      <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Paper>
            )}

            {/* Details */}
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                Details
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Stack spacing={2}>
                <Box>
                  <Typography variant="caption" color="text.secondary">Source</Typography>
                  <Typography variant="body2">{TicketSourceLabels[ticket.source] || ticket.source}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">Category</Typography>
                  <Typography variant="body2">{ticket.category || '-'}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">Assigned To</Typography>
                  <Typography variant="body2">{ticket.assigned_user_name || 'Unassigned'}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">Created</Typography>
                  <Typography variant="body2">{formatDateTime(ticket.created_at)}</Typography>
                </Box>
                {ticket.resolved_at && (
                  <Box>
                    <Typography variant="caption" color="text.secondary">Resolved</Typography>
                    <Typography variant="body2">{formatDateTime(ticket.resolved_at)}</Typography>
                  </Box>
                )}
              </Stack>
            </Paper>

            {/* Contact Info */}
            {ticket.contact && (
              <Paper sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom>
                  Contact
                </Typography>
                <Divider sx={{ mb: 2 }} />
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Avatar sx={{ bgcolor: 'primary.main' }}>
                    <PersonIcon />
                  </Avatar>
                  <Box>
                    <Typography variant="subtitle2">{ticket.contact.full_name}</Typography>
                    <Typography variant="body2" color="text.secondary">{ticket.contact.email}</Typography>
                    {ticket.contact.phone && (
                      <Typography variant="body2" color="text.secondary">{ticket.contact.phone}</Typography>
                    )}
                  </Box>
                </Box>
              </Paper>
            )}

            {/* Account Info */}
            {ticket.account && (
              <Paper sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom>
                  Account
                </Typography>
                <Divider sx={{ mb: 2 }} />
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Avatar sx={{ bgcolor: 'secondary.main' }}>
                    <BusinessIcon />
                  </Avatar>
                  <Box>
                    <Typography variant="subtitle2">{ticket.account.name}</Typography>
                  </Box>
                </Box>
              </Paper>
            )}

            {/* SLA Info */}
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                SLA Information
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Stack spacing={2}>
                <Box>
                  <Typography variant="caption" color="text.secondary">First Response Due</Typography>
                  <Typography variant="body2">
                    {ticket.sla_first_response_due ? formatDateTime(ticket.sla_first_response_due) : 'Not set'}
                  </Typography>
                  {ticket.sla_first_response_breached && (
                    <Chip label="Breached" color="error" size="small" sx={{ mt: 0.5 }} />
                  )}
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">Resolution Due</Typography>
                  <Typography variant="body2">
                    {ticket.sla_resolution_due ? formatDateTime(ticket.sla_resolution_due) : 'Not set'}
                  </Typography>
                  {ticket.sla_resolution_breached && (
                    <Chip label="Breached" color="error" size="small" sx={{ mt: 0.5 }} />
                  )}
                </Box>
              </Stack>
            </Paper>
          </Stack>
        </Grid>
      </Grid>

      {/* Edit Dialog */}
      <TicketForm
        open={editOpen}
        onClose={handleEditClose}
        ticket={ticket}
        chatbotId={chatbotId}
      />
    </Box>
  );
};

export default TicketDetail;
